import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStudio } from "./StudioContext";
import { StudioPage, Panel, SectionLabel, Empty, Loading, Badge, Drawer, DBlock, DKV } from "./StudioShared";
import { GitMerge, ArrowRight, Info, MousePointerClick } from "lucide-react";

const titleCase = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());
const pct = (n) => (typeof n === "number" ? `${Math.round(n * 100)}%` : "—");

export default function Workflows() {
  const { missions, strategists } = useStudio();
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState(null);
  const [planId, setPlanId] = useState(null);

  const designed = useMemo(() => {
    const byId = Object.fromEntries((strategists || []).map((s) => [s.id, s.name]));
    return (strategists || [])
      .filter((s) => (s.recommended_sequence || []).length > 1)
      .map((s) => ({ owner: s.name, dept: s.department, seq: s.recommended_sequence, names: s.recommended_sequence.map((id) => byId[id] || id) }));
  }, [strategists]);

  const { patterns, pairs } = useMemo(() => {
    const map = {}; const pairCount = {};
    (missions || []).forEach((m) => {
      const seq = m.document?.strategists_consulted || [];
      if (seq.length === 0) return;
      const key = seq.join(" → ");
      if (!map[key]) map[key] = { key, sequence: seq, count: 0, examples: [], plans: [], industry: m.context?.data?.industry };
      map[key].count += 1;
      map[key].plans.push(m);
      if (map[key].examples.length < 3 && m.org_name) map[key].examples.push(m.org_name);
      for (let i = 0; i < seq.length; i++) for (let j = i + 1; j < seq.length; j++) {
        const pk = [seq[i], seq[j]].sort().join(" + ");
        pairCount[pk] = (pairCount[pk] || 0) + 1;
      }
    });
    const patterns = Object.values(map).sort((a, b) => b.count - a.count);
    const pairs = Object.entries(pairCount).map(([k, v]) => ({ pair: k, count: v })).sort((a, b) => b.count - a.count).slice(0, 10);
    return { patterns, pairs };
  }, [missions]);

  const openPattern = (p) => {
    setOpenKey(p.key);
    const sorted = [...p.plans].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setPlanId(sorted[0]?.id || null);
  };
  const activePattern = patterns.find((p) => p.key === openKey);
  const plansSorted = activePattern ? [...activePattern.plans].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : [];
  const plan = plansSorted.find((m) => m.id === planId);
  const doc = plan?.document || {};
  const analysis = plan?.analysis || {};
  const close = () => { setOpenKey(null); setPlanId(null); };

  if (!missions) return <StudioPage title="Workflow Explorer" question="How does intelligence flow?"><Loading /></StudioPage>;

  return (
    <StudioPage title="Workflow Explorer" question="How does intelligence flow?" testid="workflows-explorer">
      <div className="flex items-start gap-2 text-xs text-slate-500 mb-5 bg-navy-800 border border-white/10 rounded-sm p-3" data-testid="workflows-note">
        <Info size={14} className="text-gold shrink-0 mt-0.5" />
        <span>These collaboration sequences are observed from real Mission Plans (which strategists actually worked together). Click any observed sequence to replay the actual plan AIPOS produced. SDK-declared sequences (below) come from each strategist's spec.</span>
      </div>

      {designed.length > 0 && (
        <div className="mb-6">
          <SectionLabel className="mb-2">Designed Sequences (SDK)</SectionLabel>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="workflow-designed">
            {designed.map((w, i) => (
              <Panel key={i} className="p-4" data-testid={`workflow-designed-${i}`}>
                <div className="flex items-center gap-2 mb-3"><GitMerge size={14} className="text-gold" /><span className="text-white text-sm">{w.owner}</span>{w.dept && <span className="text-slate-500 text-xs ml-auto">{w.dept}</span>}</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {w.names.map((n, j) => (
                    <span key={j} className="flex items-center gap-1.5">
                      <span className="text-xs text-slate-300 bg-white/5 border border-white/10 px-2 py-0.5 rounded-sm">{n}</span>
                      {j < w.names.length - 1 && <ArrowRight size={12} className="text-slate-600" />}
                    </span>
                  ))}
                </div>
              </Panel>
            ))}
          </div>
        </div>
      )}

      {patterns.length === 0 ? <Empty text="No workflows observed yet — generate mission plans to reveal how intelligence flows." testid="workflows-empty" /> : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SectionLabel className="mb-2">Observed Collaboration Sequences</SectionLabel>
            <p className="text-slate-500 text-xs mb-2" data-testid="workflow-replay-hint">Click a sequence to replay the real Mission Plan behind it.</p>
            <div className="space-y-3" data-testid="workflow-patterns">
              {patterns.map((p, i) => (
                <Panel key={i} onClick={() => openPattern(p)} data-testid={`workflow-pattern-${i}`}
                  className="p-4 cursor-pointer hover:border-gold/40 transition-colors group">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2"><GitMerge size={15} className="text-gold" />{p.industry && <span className="text-slate-400 text-sm">{titleCase(p.industry)}</span>}</div>
                    <div className="flex items-center gap-2">
                      <Badge tone="gold">{p.count}× used</Badge>
                      <span className="text-slate-600 group-hover:text-gold text-xs inline-flex items-center gap-1 transition-colors"><MousePointerClick size={12} /> Replay</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.sequence.map((s, j) => (
                      <span key={j} className="flex items-center gap-2">
                        <span className="text-sm text-white bg-white/5 border border-white/10 px-2.5 py-1 rounded-sm">{s}</span>
                        {j < p.sequence.length - 1 && <ArrowRight size={14} className="text-slate-600" />}
                      </span>
                    ))}
                  </div>
                  {p.examples.length > 0 && <div className="text-xs text-slate-500 mt-3">e.g. {p.examples.join(", ")}</div>}
                </Panel>
              ))}
            </div>
          </div>
          <div>
            <SectionLabel className="mb-2">Top Collaborations</SectionLabel>
            <Panel className="p-4 space-y-2.5" data-testid="workflow-pairs">
              {pairs.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">{p.pair}</span>
                  <span className="font-mono text-xs text-gold">{p.count}×</span>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      )}

      {/* Mission Replay — the real plan behind an observed sequence */}
      <Drawer open={!!activePattern} onClose={close} testid="mission-replay-drawer"
        title={plan ? plan.org_name : "Mission Replay"}
        subtitle={activePattern ? activePattern.sequence.join(" → ") : ""}>
        {activePattern && (
          <>
            {plansSorted.length > 1 && (
              <DBlock title={`Clients with this exact sequence (${plansSorted.length})`}>
                <div className="flex flex-wrap gap-1.5" data-testid="replay-plan-picker">
                  {plansSorted.map((m) => (
                    <button key={m.id} onClick={() => setPlanId(m.id)} data-testid={`replay-plan-${m.id}`}
                      className={`text-[11px] px-2 py-0.5 rounded-sm border transition-colors ${m.id === planId ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:text-white"}`}>
                      {m.org_name || "Untitled"}
                    </button>
                  ))}
                </div>
              </DBlock>
            )}
            {plan && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge tone={doc.ai_connected ? "green" : "slate"}>{doc.ai_connected ? "AI-generated" : "rule-based"}</Badge>
                  <Badge tone="gold">confidence {pct(doc.confidence)}</Badge>
                  {doc.provider_used && <Badge tone="slate">{doc.provider_used}</Badge>}
                </div>

                <DBlock title="Step 1 · Mission analysis (detected domains)">
                  {(analysis.detected_domains || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1.5" data-testid="replay-domains">
                      {analysis.detected_domains.map((d) => <span key={d} className="text-[11px] text-slate-300 bg-white/5 border border-white/10 px-2 py-0.5 rounded-sm">{d}</span>)}
                    </div>
                  ) : <p className="text-slate-500 text-xs">No domains recorded.</p>}
                </DBlock>

                <DBlock title={`Step 2 · Strategists consulted (${(doc.strategists_consulted || []).length})`}>
                  <div className="flex flex-wrap gap-1.5" data-testid="replay-strategists">
                    {(doc.strategists_consulted || []).map((s) => <span key={s} className="text-[11px] text-gold bg-gold/10 border border-gold/25 px-2 py-0.5 rounded-sm">{s}</span>)}
                  </div>
                </DBlock>

                {(doc.headline || doc.executive_summary || doc.overview) && (
                  <DBlock title="Step 3 · Synthesized recommendation">
                    {doc.headline && <p className="text-white text-sm font-medium mb-1.5">{doc.headline}</p>}
                    <p className="text-slate-300 text-sm leading-relaxed">{doc.executive_summary || doc.overview}</p>
                  </DBlock>
                )}

                {(doc.action_plan || []).length > 0 && (
                  <DBlock title={`Action plan (${doc.action_plan.length})`}>
                    <ol className="space-y-2" data-testid="replay-action-plan">
                      {doc.action_plan.map((a, i) => (
                        <li key={i} className="text-sm text-slate-300 border-l-2 border-gold/40 pl-3">
                          <span className="text-white">{a.title || a.action || a}</span>
                          {(a.owner || a.priority) && <span className="text-slate-500 text-xs ml-1">— {[a.owner, a.priority && `priority ${a.priority}`].filter(Boolean).join(" · ")}</span>}
                          {a.detail && <div className="text-slate-500 text-xs mt-0.5">{a.detail}</div>}
                        </li>
                      ))}
                    </ol>
                  </DBlock>
                )}

                <div className="space-y-1.5 border-t border-white/5 pt-3">
                  <DKV label="Recommended timeline" value={doc.recommended_timeline} />
                  <DKV label="Recommended investment" value={doc.recommended_investment} />
                  <DKV label="Created" value={plan.created_at ? new Date(plan.created_at).toLocaleString() : ""} />
                  <DKV label="Source" value={plan.consumer || plan.source} />
                </div>

                {plan.project_id && (
                  <button onClick={() => navigate(`/admin?view=projects&project=${plan.project_id}`)} data-testid="replay-open-project"
                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-gold border border-gold/40 hover:bg-gold/10 rounded-sm px-2.5 py-1 transition-colors">
                    Open project <ArrowRight size={12} />
                  </button>
                )}
              </>
            )}
          </>
        )}
      </Drawer>
    </StudioPage>
  );
}
